// ============================================================================
// GESTIMMONY — Page Abonnement (/app/abonnement)
// ============================================================================
// Affiche le statut de l'abonnement Stripe du bailleur :
//   - Plan actif (Essentiel / Patrimoine / Prestige), prix, date d'activation,
//     prochaine échéance, drapeau d'annulation programmée
//   - Bouton « Gérer mon abonnement » qui ouvre le Stripe Customer Portal
//     (changement de plan, annulation, factures, mise à jour carte)
//   - Si aucun abonnement : CTA vers /tarifs
// ============================================================================
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  CreditCard, Calendar, CheckCircle2, XCircle, ArrowRight,
  RefreshCcw, Sparkles, AlertTriangle, ExternalLink, Loader2, Lock,
} from "lucide-react";

const PLANS_INFO = {
  // Gestimmony — Mensuel sans engagement
  gestimmony_essentiel_monthly: {
    label: "Essentiel", price: 15, suffix: "HT / mois",
    features: ["Jusqu'à 3 biens", "8 modèles de courriers", "Quittances auto", "Tableau de bord", "Support email"],
    color: "bg-slate-100 text-slate-800",
  },
  gestimmony_patrimoine_monthly: {
    label: "Patrimoine", price: 24, suffix: "HT / mois",
    features: ["Jusqu'à 10 biens", "16 modèles de courriers", "Portail locataire", "Alertes IRL", "Support prioritaire"],
    color: "bg-teal-100 text-teal-800",
  },
  gestimmony_prestige_monthly: {
    label: "Prestige", price: 44, suffix: "HT / mois",
    features: ["Jusqu'à 20 biens", "Les 20 modèles", "Support prioritaire"],
    color: "bg-amber-100 text-amber-800",
  },
  // Gestimmony — Engagement annuel (-20 %)
  gestimmony_essentiel_annual: {
    label: "Essentiel (annuel)", price: 12, suffix: "HT / mois",
    features: ["Jusqu'à 3 biens", "8 modèles de courriers", "Quittances auto"],
    color: "bg-slate-100 text-slate-800",
  },
  gestimmony_patrimoine_annual: {
    label: "Patrimoine (annuel)", price: 19, suffix: "HT / mois",
    features: ["Jusqu'à 10 biens", "16 modèles de courriers", "Portail locataire", "Alertes IRL"],
    color: "bg-teal-100 text-teal-800",
  },
  gestimmony_prestige_annual: {
    label: "Prestige (annuel)", price: 35, suffix: "HT / mois",
    features: ["Jusqu'à 20 biens", "Les 20 modèles", "Support prioritaire"],
    color: "bg-amber-100 text-amber-800",
  },
};

const STATUS_LABEL = {
  active:   { label: "Actif",              color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  canceled: { label: "Annulé",             color: "bg-rose-100 text-rose-800",       icon: XCircle },
  past_due: { label: "Paiement en retard", color: "bg-amber-100 text-amber-800",     icon: AlertTriangle },
  trialing: { label: "Période d'essai",    color: "bg-sky-100 text-sky-800",         icon: Sparkles },
  incomplete: { label: "Paiement incomplet", color: "bg-amber-100 text-amber-800",   icon: AlertTriangle },
  incomplete_expired: { label: "Paiement expiré", color: "bg-rose-100 text-rose-800", icon: XCircle },
  paused:   { label: "Suspendu",           color: "bg-slate-100 text-slate-700",     icon: AlertTriangle },
  unpaid:   { label: "Impayé",             color: "bg-rose-100 text-rose-800",       icon: XCircle },
};

// Traduction des statuts de paiement (dernière transaction Stripe)
const PAYMENT_STATUS_LABEL = {
  pending: { label: "En attente de confirmation", color: "text-amber-700" },
  paid:    { label: "Payé",                       color: "text-emerald-700" },
  failed:  { label: "Échoué",                     color: "text-rose-700" },
  refunded:{ label: "Remboursé",                  color: "text-slate-700" },
  canceled:{ label: "Annulé",                     color: "text-slate-500" },
  no_payment_required: { label: "Aucun paiement requis", color: "text-slate-700" },
};

const TIERS = [
  { key: "essentiel", name: "Essentiel" },
  { key: "patrimoine", name: "Patrimoine" },
  { key: "prestige", name: "Prestige" },
];

function PlanGrid({ billing, setBilling, currentPlan, onSelect, busyKey, title, subtitle, actionLabel }) {
  return (
    <Card className="p-6" data-testid="plan-change-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h3 className="font-heading text-xl font-bold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50">
          <button onClick={() => setBilling("monthly")} data-testid="plan-billing-monthly"
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${billing === "monthly" ? "bg-white shadow-sm font-semibold text-slate-900" : "text-slate-500"}`}>Mensuel</button>
          <button onClick={() => setBilling("annual")} data-testid="plan-billing-annual"
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${billing === "annual" ? "bg-white shadow-sm font-semibold text-slate-900" : "text-slate-500"}`}>Annuel <span className="text-teal-600 text-xs font-semibold">-20%</span></button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {TIERS.map((tier) => {
          const lookup = `gestimmony_${tier.key}_${billing}`;
          const info = PLANS_INFO[lookup];
          if (!info) return null;
          const isCurrent = currentPlan === lookup;
          const highlighted = tier.key === "patrimoine";
          return (
            <div key={lookup} data-testid={`plan-card-${lookup}`}
              className={`relative rounded-xl border p-5 flex flex-col ${highlighted ? "border-teal-300 ring-1 ring-teal-200" : "border-slate-200"} ${isCurrent ? "bg-teal-50/40" : "bg-white"}`}>
              {highlighted && <div className="absolute -top-2.5 left-5 text-[10px] font-bold uppercase tracking-wider bg-teal-600 text-white px-2 py-0.5 rounded">Populaire</div>}
              <div className="font-heading font-bold text-lg text-slate-900">{tier.name}</div>
              <div className="mt-1 mb-3">
                <span className="text-3xl font-bold text-slate-900">{info.price}</span>
                <span className="text-sm text-slate-500"> € {info.suffix}</span>
                {billing === "annual" && (
                  <div className="text-xs text-slate-500 mt-1">soit {info.price * 12} € HT/an · engagement 12 mois</div>
                )}
                <div className="text-[11px] text-slate-400 mt-1">+ TVA selon votre territoire (ajoutée au paiement)</div>
              </div>
              <ul className="space-y-1.5 flex-1">
                {info.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-teal-600 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              {isCurrent ? (
                <Button disabled variant="outline" className="mt-4" data-testid={`plan-current-${lookup}`}>Formule actuelle</Button>
              ) : (
                <Button onClick={() => onSelect(lookup)} disabled={!!busyKey}
                  className={`mt-4 ${highlighted ? "bg-[hsl(var(--primary))]" : ""}`} variant={highlighted ? "default" : "outline"}
                  data-testid={`plan-choose-${lookup}`}>
                  {busyKey === lookup ? <Loader2 className="h-4 w-4 animate-spin" /> : actionLabel}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function SubscriptionPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [billing, setBilling] = useState("monthly");
  const [changing, setChanging] = useState(null);
  const [confirmPlan, setConfirmPlan] = useState(null);
  const [subscribing, setSubscribing] = useState(null);

  const subscribe = async (lookup) => {
    setSubscribing(lookup);
    try {
      const { data: res } = await api.post("/payments/checkout", {
        lookup_key: lookup, origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error("Erreur : " + (e.response?.data?.detail || e.message));
      setSubscribing(null);
    }
  };

  const changePlan = async (lookup) => {
    setChanging(lookup);
    try {
      await api.post("/payments/change-plan", { lookup_key: lookup });
      toast.success("Formule mise à jour");
      setConfirmPlan(null);
      load();
    } catch (e) {
      toast.error("Erreur : " + (e.response?.data?.detail || e.message));
    } finally { setChanging(null); }
  };

  const load = () => {
    setLoading(true);
    api.get("/payments/subscription")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Impossible de charger votre abonnement"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openPortal = async () => {
    setOpening(true);
    try {
      const { data: res } = await api.post("/payments/portal");
      window.location.href = res.portal_url;
    } catch (e) {
      toast.error("Erreur : " + (e.response?.data?.detail || e.message));
      setOpening(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="subscription-page">
        <div className="h-8 w-64 bg-slate-100 rounded animate-pulse" />
        <div className="h-64 bg-slate-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  const planInfo = PLANS_INFO[data?.plan] || null;
  const statusInfo = STATUS_LABEL[data?.status] || { label: "Aucun abonnement", color: "bg-slate-100 text-slate-600", icon: XCircle };
  const StatusIcon = statusInfo.icon;
  const isActiveSub = (data?.status === "active" || data?.status === "trialing") && data?.has_stripe_customer;

  // Résiliation programmée (drapeau Stripe cancel_at_period_end OU date cancel_at)
  const canceling = !!data?.cancel_at_period_end;
  // Date de fin effective : date de résiliation si planifiée, sinon fin de la
  // période en cours (toujours affichée, y compris pour les plans mensuels).
  const endDate = data?.cancellation_effective_date || data?.current_period_end;
  const midLabel = canceling
    ? "Fin de l'abonnement"
    : (data?.in_trial ? "Fin de l'essai gratuit" : "Prochaine échéance");
  const midDate = canceling
    ? endDate
    : (data?.in_trial ? (data?.trial_end || data?.current_period_end) : data?.current_period_end);

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Abonnement</h1>
        <p className="text-sm text-slate-500 mt-1">
          Gérez votre formule, votre moyen de paiement et votre facturation en toute autonomie.
        </p>
      </div>

      {/* ─── État courant ──────────────────────────────────────────── */}
      <Card className="p-6" data-testid="subscription-current-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-4">
            <div className={`h-12 w-12 rounded-xl ${planInfo?.color || "bg-slate-100"} flex items-center justify-center`}>
              <CreditCard className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-heading text-2xl font-bold text-slate-900">
                  {planInfo?.label || "Aucune formule"}
                </h2>
                <Badge variant="outline" className={statusInfo.color} data-testid="subscription-status-badge">
                  <StatusIcon className="h-3 w-3 mr-1" /> {statusInfo.label}
                </Badge>
              </div>
              {planInfo && (
                <p className="text-slate-600 mt-1">
                  <span className="text-3xl font-bold text-slate-900">{planInfo.price.toFixed(2).replace(".", ",")} €</span>
                  <span className="text-sm text-slate-500"> {planInfo.suffix || "/ mois TTC"}</span>
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {data?.has_stripe_customer ? (
              <Button
                data-testid="subscription-portal-btn"
                onClick={openPortal}
                disabled={opening}
                className="bg-[hsl(var(--primary))]"
              >
                {opening ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
                Factures & paiement
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" onClick={load} data-testid="subscription-refresh-btn">
              <RefreshCcw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {planInfo && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-stone-200">
            <div data-testid="subscription-since">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Actif depuis
              </div>
              <div className="text-sm font-medium text-slate-900 mt-1">{formatDate(data?.since)}</div>
            </div>
            <div data-testid="subscription-next">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold flex items-center gap-1">
                {canceling ? <XCircle className="h-3 w-3" /> : <RefreshCcw className="h-3 w-3" />} {midLabel}
              </div>
              <div className={`text-sm font-medium mt-1 ${canceling ? "text-amber-700" : "text-slate-900"}`}>
                {formatDate(midDate)}
              </div>
            </div>
            <div data-testid="subscription-cancel-flag">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Annulation programmée
              </div>
              <div className="text-sm font-medium text-slate-900 mt-1">
                {canceling
                  ? <span className="text-amber-700 font-semibold" data-testid="subscription-cancel-yes">Oui — fin le {formatDate(endDate)}</span>
                  : <span className="text-emerald-700" data-testid="subscription-cancel-no">Non</span>}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* ─── Fonctionnalités incluses ─────────────────────────────── */}
      {planInfo && (
        <Card className="p-6" data-testid="subscription-features-card">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Ce que comprend votre formule</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {planInfo.features.map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                <CheckCircle2 className="h-4 w-4 text-teal-600 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* ─── Formules (style Tarifs, intégré à l'app) ───────────────── */}
      {isActiveSub && !data?.in_commitment_period && (
        <PlanGrid billing={billing} setBilling={setBilling} currentPlan={data?.plan}
          onSelect={setConfirmPlan} busyKey={changing}
          title="Changer de formule"
          subtitle="Effet immédiat, ajustement au prorata sur votre prochaine facture."
          actionLabel="Choisir cette formule" />
      )}
      {!isActiveSub && (
        <PlanGrid billing={billing} setBilling={setBilling} currentPlan={null}
          onSelect={subscribe} busyKey={subscribing}
          title="Choisissez votre formule"
          subtitle={billing === "annual" ? "14 jours d'essai offert, puis engagement 12 mois." : "Paiement immédiat, sans engagement."}
          actionLabel={billing === "annual" ? "Commencer l'essai" : "S'abonner"} />
      )}
      {isActiveSub && data?.in_commitment_period && (
        <Card className="p-5 bg-amber-50 border-amber-200 text-sm text-amber-900" data-testid="plan-change-locked">
          Le changement de formule sera disponible à la fin de votre période d'engagement. Pour vos factures et votre moyen de paiement, utilisez « Factures & paiement ».
        </Card>
      )}

      {/* Confirmation changement de formule */}
      <AlertDialog open={!!confirmPlan} onOpenChange={(o) => !o && setConfirmPlan(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer le changement de formule ?</AlertDialogTitle>
            <AlertDialogDescription>
              Votre abonnement passera à « {PLANS_INFO[confirmPlan]?.label} » ({PLANS_INFO[confirmPlan]?.price} € {PLANS_INFO[confirmPlan]?.suffix}).
              La différence est ajustée au prorata sur votre prochaine facture. Effet immédiat.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="plan-change-confirm" onClick={() => changePlan(confirmPlan)}>Confirmer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ─── Dernière facture ─────────────────────────────────────── */}
      {data?.last_payment && (
        <Card className="p-6" data-testid="subscription-last-payment-card">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Dernier paiement</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Date</div>
              <div className="font-medium text-slate-900 mt-0.5">{formatDate(data.last_payment.date)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Montant</div>
              <div className="font-medium text-slate-900 mt-0.5">
                {((data.last_payment.amount || 0) / 100).toFixed(2).replace(".", ",")} €
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Statut</div>
              {(() => {
                const p = PAYMENT_STATUS_LABEL[data.last_payment.status] || { label: data.last_payment.status || "—", color: "text-slate-700" };
                return <div className={`font-medium mt-0.5 ${p.color}`}>{p.label}</div>;
              })()}
            </div>
            <div className="text-right">
              {data?.has_stripe_customer && (
                <Button variant="outline" size="sm" onClick={openPortal} data-testid="subscription-invoices-btn">
                  Toutes les factures <ExternalLink className="h-3.5 w-3.5 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ─── Bandeau engagement (uniquement si plan avec engagement) ───────── */}
      {data?.is_commitment && (
        <Card
          data-testid="subscription-commitment-card"
          className="p-5 bg-amber-50 border-amber-200"
        >
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <Lock className="h-4 w-4 text-amber-700" />
            </div>
            <div className="text-sm text-amber-900 leading-relaxed">
              <div className="font-semibold text-amber-900">
                Engagement {data?.commitment_months === 12 ? "1 an" : `${data?.commitment_months} mois`} — jusqu'au {formatDate(data?.commitment_end)}
              </div>
              <p className="mt-1 text-amber-800">
                Vous bénéficiez du tarif préférentiel réservé aux abonnements avec engagement.
                En contrepartie, la résiliation ne prend effet qu'à la <b>fin de la période
                d'engagement en cours</b>. Toute demande de résiliation faite avant cette date
                sera effective au {formatDate(data?.commitment_end)}.
              </p>
              <p className="mt-2 text-xs text-amber-700">
                À l'approche de l'échéance, vous serez notifié par e-mail pour choisir de
                renouveler votre engagement (tarif préférentiel conservé) ou de basculer vers
                la formule mensuelle sans engagement.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* ─── Aide ──────────────────────────────────────────────────── */}
      <Card className="p-5 bg-slate-50 border-slate-200">
        <p className="text-xs text-slate-600 leading-relaxed">
          <b>Autonomie totale</b> : depuis le bouton « Gérer mon abonnement », vous pouvez à tout moment
          changer de formule, mettre à jour votre carte bancaire et consulter vos factures.
          {data?.is_commitment ? (
            <> Pour les abonnements <b>avec engagement</b>, la résiliation n'est pas
            immédiate : elle prend effet à la fin de la période d'engagement en cours
            (voir bandeau ci-dessus).</>
          ) : (
            <> L'annulation d'un abonnement sans engagement est possible à tout moment et
            prend effet à la fin de la période mensuelle en cours.</>
          )}
        </p>
      </Card>
    </div>
  );
}
